<template>
    <div
        v-if="!loading && !error && products.length > 0"
        class="all-products-section"
    >
        <h2><i class="pi pi-box"></i> All Products</h2>
        <div class="products-grid">
            <Card
                v-for="product in products"
                :key="product.identity"
                class="product-card"
                @click="navigateToProduct(product.identity)"
            >
                <template #title>{{
                    product.name || product.identity
                }}</template>
                <template #content>
                    <div v-if="product.metrics" class="metrics-preview">
                        <div class="metric">
                            <span class="metric-label">Total Events:</span>
                            <span class="metric-value">{{
                                product.metrics.total_events
                            }}</span>
                        </div>
                        <div class="metric">
                            <span class="metric-label">Error Rate:</span>
                            <span class="metric-value"
                                >{{
                                    product.metrics.error_rate.toFixed(2)
                                }}%</span
                            >
                        </div>
                    </div>
                </template>
            </Card>
        </div>
    </div>
    <div v-else-if="loading" class="loading">
        <Spinner />
    </div>
    <div v-else-if="error" class="error">
        <Message severity="error">{{ error }}</Message>
    </div>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import Message from "primevue/message";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useClient, useServices } from "@/lib/openapi";
import type { LiveMetrics } from "@/lib/openapi/gen/types.gen";
import Spinner from "@/shared/Spinner.vue";

defineOptions({
    name: "AllProductsSection",
});

const props = defineProps<{
    domainId: string;
    hours: number;
    stekRelease?: string | null;
}>();

const route = useRoute();
const router = useRouter();
const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});
const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const products = ref<
    Array<{ identity: string; name?: string; metrics?: LiveMetrics }>
>([]);
const loading = ref(false);
const error = ref<string | null>(null);

const hasValidRouteParams = computed(() =>
    Boolean(route.params.orgName && route.params.stekName)
);

async function fetchProducts() {
    loading.value = true;
    error.value = null;
    try {
        const productsResult =
            await api.startProductProductMgtGetProductsByDomainStartPost({
                body: { domain_id: props.domainId },
            });
        if (productsResult.response.ok && productsResult.data) {
            let productsData: Array<Record<string, unknown>> = [];
            if (Array.isArray(productsResult.data)) {
                productsData = productsResult.data as Array<
                    Record<string, unknown>
                >;
            } else if (
                productsResult.data &&
                typeof productsResult.data === "object" &&
                "items" in (productsResult.data as Record<string, unknown>) &&
                Array.isArray(
                    (productsResult.data as { items: unknown[] }).items
                )
            ) {
                productsData = (
                    productsResult.data as {
                        items: Array<Record<string, unknown>>;
                    }
                ).items;
            }

            // Fetch metrics for each product
            const productsWithMetrics = await Promise.all(
                productsData.map(async (p: Record<string, unknown>) => {
                    const identity = String(p.identity || p.id || p);
                    const name = String(p.name || p.title || "");
                    try {
                        const response = await client.post<LiveMetrics>({
                            url: "/stek/live_mgt/get_metrics/start",
                            body: {
                                product_id: identity,
                                hours: props.hours,
                                stek_release: props.stekRelease,
                            },
                        });

                        if (!response.response.ok || !response.data) {
                            return {
                                identity,
                                name,
                            };
                        }

                        const metrics = response.data;
                        return {
                            identity,
                            name,
                            metrics,
                        };
                    } catch {
                        return {
                            identity,
                            name,
                        };
                    }
                })
            );
            products.value = productsWithMetrics;
        }
    } catch (e) {
        error.value =
            e instanceof Error ? e.message : "Failed to load products";
    } finally {
        loading.value = false;
    }
}

function navigateToProduct(productId: string) {
    if (!hasValidRouteParams.value) return;

    const orgName = route.params.orgName;
    const stekName = route.params.stekName;
    router.push({
        name: "liveProductPage",
        params: { orgName, stekName, productId },
    });
}

onMounted(() => {
    fetchProducts();
});

watch([() => props.hours, () => props.stekRelease], () => {
    fetchProducts();
});
</script>

<style scoped lang="scss">
.all-products-section {
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 2px solid var(--surface-border);

    h2 {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
            font-size: 1.2rem;
        }
    }
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
}

.product-card {
    cursor: pointer;
    transition:
        transform 0.2s,
        box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.metrics-preview {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.metric {
    display: flex;
    justify-content: space-between;
}

.metric-label {
    font-weight: 500;
}

.metric-value {
    font-weight: bold;
}

.loading,
.error {
    text-align: center;
    padding: 2rem;
}
</style>
